import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import { toast } from "sonner";
import { Loader2, ArrowLeft, FolderOpen, Heart } from "lucide-react";
import { Card, CardContent } from "@/components/uiDir/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/uiDir/avatar";
import { Button } from "@/components/uiDir/button";
import { Badge } from "@/components/uiDir/badge";

interface Project {
  id: string;
  title: string;
  description: string;
  cover_image: string;
  tags: string[];
  likes_count: number;
  views_count: number;
  author_username: string;
  author_name: string;
  author_avatar: string;
}

const getInitials = (name: string) => {
    if (!name) return '??';
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
};

const AllProjects = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchAllProjects = async () => {
      try {
        // This RPC function needs to be created in your Supabase dashboard.
        // See SQL snippet below.
        const { data, error } = await supabase.rpc('get_all_projects');

        if (error) {
          throw error;
        }

        setProjects(data || []);
      } catch (error) {
        console.error("Error fetching all projects:", error);
        toast.error("Failed to load projects.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllProjects();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary mb-4" />
          <p className="text-muted-foreground">Loading All Projects...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-10 max-w-7xl">
        <div className="mb-8">
          <Link to="/dashboard">
            <Button variant="ghost" className="mb-4">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Dashboard
            </Button>
          </Link>
          <h1 className="text-4xl font-heading font-bold mb-2">Discover Projects</h1>
          <p className="text-muted-foreground">Explore all projects from creators on PortfolioHub.</p>
        </div>

        {projects.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {projects.map((project) => (
              <Link key={project.id} to={`/profile/${project.author_username}#${project.id}`}>
                <Card className="group cursor-pointer hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden border-0 rounded-xl">
                  <div className="relative h-48 bg-gray-200">
                    {project.cover_image ? (
                      <img src={project.cover_image} alt={project.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-100"><FolderOpen className="h-12 w-12 text-gray-400" /></div>
                    )}
                    <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1 backdrop-blur-sm"><Heart className="h-3 w-3 fill-current" />{project.likes_count}</div>
                  </div>
                  <CardContent className="p-4">
                    <p className="font-semibold text-base line-clamp-1 mb-1">{project.title}</p>
                    <div className="flex items-center gap-2 mb-3">
                      <Avatar className="h-6 w-6"><AvatarImage src={project.author_avatar} /><AvatarFallback>{getInitials(project.author_name)}</AvatarFallback></Avatar>
                      <p className="text-xs text-gray-600 truncate">@{project.author_username}</p>
                    </div>
                    {project.tags && project.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">{(project.tags || []).slice(0, 3).map((tag, i) => (<Badge key={i} variant="secondary" className="text-xs">{tag}</Badge>))}</div>
                    )}
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <Card className="glass-card text-center py-12">
            <CardContent>
              <FolderOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-lg font-semibold">No Projects Found</h2>
              <p className="text-sm text-muted-foreground">Check back later to see new work from creators.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AllProjects;